import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import usePageVisibility from "../hooks/usePageVisibility";
import { onFirstInteraction, hasInteracted } from "../utils/firstInteraction";

const REDUCED_MOTION =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function Carousel({
  slides,
  heroHeight,
  autoPlayInterval = 7000,
  className = "",
  onCycleComplete,
  skipIntro = false,
  introComplete = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(skipIntro ? 1 : 0);
  const [direction, setDirection] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const prevIndex = useRef(currentIndex);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const initialRender = useRef(true);

  const isPageVisible = usePageVisibility();

  // Auto-play (and next-slide image preloading) waits for the first user
  // gesture: each rotation pulls a 100-200KB background, which kept the
  // viewport visually changing for 30s+ in Lighthouse traces (Speed Index 22s).
  const [interacted, setInteracted] = useState(hasInteracted());
  useEffect(() => {
    onFirstInteraction(() => setInteracted(true));
  }, []);

  // Add a ref to store all preloaded image elements
  const preloadedImages = useRef({});

  // Add state to track if intro slide has been shown
  const [introShown, setIntroShown] = useState(skipIntro);

  // Mark intro as shown once we transition to slide 1
  useEffect(() => {
    if (currentIndex === 1 && !introShown) {
      // Wait for transition to complete before marking intro as shown
      const timer = setTimeout(() => {
        setIntroShown(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, introShown]);

  // Preload first slide immediately and subsequent slides ahead of time
  useEffect(() => {
    const slidesToPreload = [];
    
    // Always try to preload the first content slide on mount if intro is active
    if (currentIndex === 0) {
      const firstSlide = slides.find(s => s.id === 1);
      if (firstSlide) slidesToPreload.push(firstSlide);
    } else {
      // Preload current; the next slide only once auto-play can actually run
      slidesToPreload.push(slides[currentIndex]);
      if (interacted) {
        const nextIndex = (currentIndex + 1) % slides.length;
        slidesToPreload.push(slides[nextIndex]);
      }
    }

    slidesToPreload.forEach(slide => {
      if (slide && slide.preloadImages) {
        slide.preloadImages.forEach(imgUrl => {
          if (!preloadedImages.current[imgUrl]) {
            const img = new Image();
            img.src = imgUrl;
            preloadedImages.current[imgUrl] = img;
          }
        });
      }
    });
  }, [currentIndex, slides, interacted]);

  // Cleanup preloaded images on unmount
  useEffect(() => {
    return () => {
      preloadedImages.current = {};
    };
  }, []);

  // Auto advance interval - with intro slide timing
  useEffect(() => {
    // Clear any existing intervals first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't set up any timers while the tab is hidden — prevents queued
    // callbacks from firing in rapid succession when the user Alt+Tabs back.
    if (!isPageVisible) return;

    // Intro slide: wait until App signals the intro animation is done,
    // then hold for 1.5s so the user can appreciate the full reveal.
    if (currentIndex === 0 && !introShown) {
      if (!introComplete) return; // intro still playing — don't advance yet
      intervalRef.current = setTimeout(() => {
        setDirection(1);
        setCurrentIndex(1);
      }, 1000);
    }
    // Regular slides: use normal interval timing
    else {
      if (!interacted || REDUCED_MOTION) return;
      intervalRef.current = setInterval(() => {
        if (!isPaused) {
          let nextIndex;

          if (introShown && (currentIndex === slides.length - 1 || currentIndex === 0)) {
            // Skip intro slide when cycling
            nextIndex = 1;
          } else {
            nextIndex = (currentIndex + 1) % slides.length;
          }



          setDirection(1);
          setCurrentIndex(nextIndex);
        }
      }, autoPlayInterval);
    }

    // Clean up on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        if (currentIndex === 0 && !introShown) {
          clearTimeout(intervalRef.current);
        } else {
          clearInterval(intervalRef.current);
        }
        intervalRef.current = null;
      }
    };
  }, [currentIndex, isPaused, autoPlayInterval, slides, introShown, isPageVisible, introComplete, interacted]);

  // Detect when the slide wraps from the last slide to the first
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }
    prevIndex.current = currentIndex;
  }, [currentIndex, slides.length, onCycleComplete]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
    }),
  };

  // Determine if dots navigation should be shown (when not on intro slide)
  const shouldShowDots = currentIndex >= 1;

  // Get non-intro slides for dot navigation
  const dotSlides = slides.slice(1);

  /* ── Touch swipe handlers ── */
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;

    // Only trigger if horizontal swipe is dominant and exceeds threshold
    if (Math.abs(dx) < 50 || Math.abs(dy) > Math.abs(dx)) return;

    if (dx < 0) {
      // Swipe left → next slide
      let nextIndex;
      if (introShown && (currentIndex === slides.length - 1 || currentIndex === 0)) {
        nextIndex = 1;
      } else {
        nextIndex = (currentIndex + 1) % slides.length;
      }
      setDirection(1);
      setCurrentIndex(nextIndex);
    } else {
      // Swipe right → previous slide
      let prevIdx;
      if (introShown && (currentIndex <= 1)) {
        prevIdx = slides.length - 1;
      } else {
        prevIdx = currentIndex - 1;
      }
      setDirection(-1);
      setCurrentIndex(prevIdx);
    }
  };

  return (
    <div
      className={`relative w-full overflow-hidden ${className}`}
      style={{
        height: heroHeight ? `${heroHeight}px` : "100vh",
        minHeight: "300px",
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { duration: 0.2, ease: [0.16, 1, 0.3, 1] },
            opacity: { duration: 0.15 },
          }}
          className="absolute inset-0 w-full h-full isolate"
          style={{ willChange: "transform, opacity" }}
        >
          {slides[currentIndex].content}
        </motion.div>
      </AnimatePresence>

      {/* Conditionally render the dots navigation - ALWAYS excluding intro */}
      {shouldShowDots && (
        <div
          className="absolute bottom-2 left-8 hidden md:flex items-center gap-2 z-[30]"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Only show dots for content slides */}
          {dotSlides.map((_, index) => {
            // Convert to actual slide index
            const slideIndex = index + 1;

            return (
              <div
                key={index}
                className="py-4 px-1 cursor-pointer flex items-center"
                onClick={() => {
                  setDirection(slideIndex > currentIndex ? 1 : -1);
                  setCurrentIndex(slideIndex);
                }}
              >
                <button
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-1 transition-all duration-300 rounded-sm pointer-events-none ${
                    slideIndex === currentIndex
                      ? "w-[38px] bg-white"
                      : "w-[12px] bg-white/30 hover:bg-white/50"
                  }`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
